import { format } from "date-fns";
import { useContext } from "react";
import { Context } from "../components/Cache";
import { formatLocation, LOCATION_FIELD_TYPE } from "../components/Location/locationUtils";

export const processError = (e, defaultString) => {
  return (
    (e && e.response && e.response.data && e.response.data.error) ||
    defaultString
  );
};

export const sentenceCap = (input) => {
  if (!input || input.length === 0 || !input.charAt) {
    return input;
  }
  return input.charAt(0).toUpperCase() + input.slice(1);
};

export const ellipsize = (input, max) => {
  return input.length > max ? `${input.substring(0, max - 3)}...` : input;
};

const getFormattedName = (member, short) => {
  const firstName = member?.firstName || "";
  const lastName = member?.lastName || "";
  if (short) {
    return `${firstName} ${lastName.charAt(0)}`.trim();
  }
  return `${firstName} ${lastName}`.trim();
};

function useFetchFieldValueForAnalytics() {
  const fetchMemberName = useFetchMemberName();
  const fetchFieldValue = useFetchFieldValue();
  return (field, incident) => {
    switch (field.type) {
      case "otherMembers": {
        let allMembers = [];
        allMembers.push(fetchMemberName(incident["memberResponding"]));
        if (incident["otherMembers"]) {
          allMembers = allMembers.concat(
            ...incident["otherMembers"].map((otherMember) =>
              fetchMemberName(otherMember)
            )
          );
        }
        return allMembers;
      }
      default:
        return fetchFieldValue(field, incident);
    }
  };
}

function useFetchFieldValue() {
  const fetchMemberName = useFetchMemberName();
  const fetchMemberFieldValue = (value) => {
    const memberName = fetchMemberName(value);
    return memberName === "Unknown user" ? sentenceCap(value) : memberName;
  };
  return (field, incident) => {
    switch (field.type) {
      case "memberResponding":
      case "member":
        return fetchMemberFieldValue(incident[field.id]);
      case "otherMembers":
        return incident[field.id]
          ? incident[field.id]
              .map((otherMember) => fetchMemberName(otherMember))
              .join(", ")
          : "";
      case "datetime":
        return incident[field.id]
          ? format(new Date(incident[field.id]), "MMM dd, yyyy h:mma")
          : "";
      case LOCATION_FIELD_TYPE:
        return formatLocation(incident[field.id]);
      default:
        return sentenceCap(incident[field.id]);
    }
  };
}

function useFetchMemberName() {
  const { state } = useContext(Context);
  return (id, short) => {
    if (!state.memberList) {
      return id;
    }
    const member = state.memberList.find((member) => member.id === id);
    if (!member) {
      return "Unknown user";
    }
    if (member.deleted) {
      return "Deleted user";
    }

    return getFormattedName(member, short);
  };
}

function usePrimaryColor() {
  const { state } = useContext(Context);
  const primary = state.config ? state.config.appearance.primaryColor : "cyan";
  return primary;
}

const SPINNER_COLOR = "#486581";

export {
  getFormattedName,
  usePrimaryColor,
  useFetchMemberName,
  useFetchFieldValue,
  useFetchFieldValueForAnalytics,
  SPINNER_COLOR,
};
